package org.chiangmeng.qfast.core.ui.customer

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.chiangmeng.qfast.core.domain.ports.QueueRepository
import org.chiangmeng.qfast.core.domain.usecases.QueueStatusUseCase

class CustomerViewModel(
    private val repository: QueueRepository,
    private val statusUseCase: QueueStatusUseCase
) : ScreenModel {

    private val _state = MutableStateFlow(CustomerContract.State())
    val state: StateFlow<CustomerContract.State> = _state.asStateFlow()

    private var observeShopJob: Job? = null
    private var observeQueuesJob: Job? = null

    fun handleIntent(intent: CustomerContract.Intent) {
        when (intent) {
            is CustomerContract.Intent.LoadShop -> loadShop(intent.shopId)
            is CustomerContract.Intent.UpdateTicketInput -> updateTicketInput(intent.input)
            is CustomerContract.Intent.IssueTicket -> issueTicket(intent.category)
            CustomerContract.Intent.DismissSuccessDialog -> {
                _state.update { it.copy(newlyIssuedTicket = null) }
            }
            is CustomerContract.Intent.StopObserving -> stopObserving()
        }
    }

    private fun stopObserving() {
        observeShopJob?.cancel()
        observeShopJob = null
        observeQueuesJob?.cancel()
        observeQueuesJob = null
    }

    private fun loadShop(shopId: String) {
        _state.update { it.copy(isLoading = true) }
        observeShopJob?.cancel()
        observeShopJob = repository.observeShop(shopId)
            .onEach { shop ->
                _state.update {
                    it.copy(
                        shop = shop,
                        isLoading = false
                    )
                }
                updateStatusMessage()
            }
            .launchIn(screenModelScope)

        observeQueuesJob?.cancel()
        observeQueuesJob = repository.observeActiveQueues(shopId)
            .onEach { queues ->
                _state.update { it.copy(queues = queues) }
                updateStatusMessage()
            }
            .launchIn(screenModelScope)
    }

    private fun updateTicketInput(input: String) {
        _state.update { it.copy(customerTicketInput = input) }
        updateStatusMessage()
    }

    private fun issueTicket(category: String) {
        val shopId = _state.value.shop?.id ?: return
        screenModelScope.launch {
            try {
                val newNumber = repository.issueTicket(shopId, category)
                _state.update {
                    it.copy(
                        newlyIssuedTicket = newNumber.toString(),
                        customerTicketInput = newNumber.toString()
                    )
                }
                updateStatusMessage()
            } catch (e: Exception) {
                // handle error if needed
            }
        }
    }

    private fun updateStatusMessage() {
        val currentState = _state.value
        val shop = currentState.shop ?: return
        val input = currentState.customerTicketInput

        if (input.isBlank()) {
            _state.update { it.copy(statusMessage = null) }
            return
        }

        // ค้นหาบัตรคิวในรายการ Active Queues
        val myTicket = currentState.queues.find { 
            it.number.toString() == input || "${it.category}${it.number}".equals(input, ignoreCase = true)
        }
        var message: String? = null

        if (myTicket != null) {
            when (myTicket.status) {
                "serving" -> message = "ถึงคิวของคุณแล้ว (NOW SERVING)"
                "skipped" -> message = "คิวถูกข้าม (SKIPPED)"
                "waiting" -> {
                    // ดึงคิวล่าสุดที่ร้านกำลังเรียกในหมวดหมู่ (category) เดียวกัน
                    val currentServingNum = shop.currentServings[myTicket.category]
                    
                    if (currentServingNum != null && myTicket.number.toString() == currentServingNum.toString()) {
                        message = "ถึงคิวของคุณแล้ว (NOW SERVING)"
                    } else {
                        // ไม่ต้องคำวณ queuesAhead เพราะอาจมีการเรียกสลับตามขนาดโต๊ะ
                        message = if (currentServingNum != null && currentServingNum > 0) {
                            "กำลังรอเรียกคิว (คิวปัจจุบัน: ${myTicket.category}$currentServingNum)"
                        } else {
                            "กำลังรอเรียกคิว..."
                        }
                    }
                }
            }
        } else {
            message = "ไม่พบข้อมูลคิว หรือคิวผ่านไปแล้ว"
        }

        _state.update { it.copy(statusMessage = message) }
    }
}
